import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "terraform",
  name: "Terraform / IaC",
  nameEn: "Terraform & IaC",
  seo: {
    title:
      "Terraform / IaCを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "Terraformを使ったInfrastructure as Codeを実践的に学べるゆめさくエンジニア。HCL・モジュール化・マルチクラウド対応まで、インフラ自動化スキルを習得しましょう。",
    url: "/columns/terraform-programming-school",
  },
  hero: {
    title: "Terraform / IaCを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Terraform & IaC",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "Terraform / IaC" },
  ],
  overview: {
    heading: "Terraform / IaCとは？",
    paragraphs: [
      "Terraform は、HashiCorp社が開発したオープンソースのInfrastructure as Code（IaC）ツールです。HCL（HashiCorp Configuration Language）という独自の宣言型言語を使い、クラウドインフラをコードとして定義・管理します。",
      "Infrastructure as Code（IaC）とは、サーバー、ネットワーク、データベースなどのインフラ構成をコードで記述し、バージョン管理・自動化・再現性を実現する手法です。手作業によるインフラ構築のミスや属人化を排除し、安全で効率的なインフラ運用を可能にします。",
      "Terraformの最大の強みはマルチクラウド対応です。AWS、GCP、Azure、Cloudflareなど、複数のクラウドプロバイダーを統一的な記法で管理でき、モジュール化によってインフラ構成の再利用も容易です。",
    ],
    useCases: [
      {
        label: "クラウドインフラの自動構築",
        description:
          "VPC、EC2、RDS、S3などのAWSリソースをTerraformで定義し、コマンド一つで環境全体を構築・更新・削除できます。",
      },
      {
        label: "マルチ環境管理",
        description:
          "開発・ステージング・本番環境を同じTerraformコードから生成し、環境間の差異をなくした一貫したインフラ管理を実現します。",
      },
      {
        label: "インフラのバージョン管理",
        description:
          "インフラ構成をGitで管理し、変更履歴の追跡、コードレビュー、ロールバックを可能にします。",
      },
      {
        label: "コンプライアンス・セキュリティの自動化",
        description:
          "セキュリティポリシーをTerraformコードに組み込み、非準拠のインフラ構成を自動的に検出・防止します。",
      },
    ],
  },
  whyLearn: {
    heading: "Terraform / IaCを学ぶべき3つの理由",
    items: [
      {
        title: "IaCはクラウド時代の必須スキル",
        description:
          "クラウドインフラの管理にIaCを採用する企業は急増しており、Terraformはその中で最も広く使われているツールです。インフラエンジニアだけでなく、バックエンドエンジニアにも求められるスキルになっています。",
      },
      {
        title: "マルチクラウド対応で汎用性が高い",
        description:
          "TerraformはAWS、GCP、Azureなど複数のクラウドに対応しているため、一度学べばどのクラウド環境でも活用できます。特定のクラウドに縛られない汎用的なスキルとして長期的に価値があります。",
      },
      {
        title: "DevOps・SRE職への強力な武器になる",
        description:
          "Terraformスキルを持つエンジニアは、DevOpsやSREのポジションで即戦力として評価されます。CI/CDパイプラインとの連携やGitOps実践と組み合わせることで、年収アップにも直結します。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでTerraform / IaCを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、上級Phaseのカリキュラムとして Terraform / IaCを学びます。AWSの基礎知識を身につけた上で、Terraformによるインフラのコード化と自動管理を実践的に学びます。",
      "単にリソースを定義するだけでなく、モジュール設計、ステート管理、CI/CDとの統合まで含めた本番運用レベルのTerraformスキルを習得します。",
    ],
    topics: [
      "HCL（HashiCorp Configuration Language）の基礎",
      "AWSリソース（VPC・EC2・RDS・S3）のTerraform定義",
      "モジュール化による再利用可能なインフラ設計",
      "Terraform State管理とリモートバックエンド",
      "変数・出力・データソースの活用",
      "CI/CDパイプラインとの統合（Terraform Cloud / GitHub Actions）",
      "実践プロジェクト：本番相当のAWS環境をTerraformで構築",
    ],
  },
  careerPaths: {
    heading: "Terraform / IaCを活かせるキャリア",
    items: [
      {
        title: "インフラエンジニア",
        description:
          "Terraformを使ったクラウドインフラの設計・構築・運用を担当します。IaCによる自動化と再現性の高いインフラ管理で、組織のクラウド運用を効率化します。",
        salary: "550〜950万円",
      },
      {
        title: "SRE（Site Reliability Engineer）",
        description:
          "TerraformによるIaCとKubernetesなどのコンテナ技術を組み合わせ、サービスの信頼性と可用性を担保します。自動化とオブザーバビリティの専門家として高い需要があります。",
        salary: "700〜1,200万円",
      },
      {
        title: "DevOpsエンジニア",
        description:
          "Terraformを中心としたインフラ自動化とCI/CDパイプラインの構築を担当します。開発チームとインフラチームの橋渡し役として、デリバリー速度と品質の向上に貢献します。",
        salary: "600〜1,000万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "6〜10ヶ月（クラウドの前提知識も含めると長期化）",
        luagate: "3〜4ヶ月（AWS基礎と合わせて効率的に学習）",
      },
      {
        label: "挫折率",
        selfStudy: "State管理やモジュール設計で複雑さに圧倒される",
        luagate: "段階的にリソースを増やしながら実践的に学習",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "EC2一台の構築で終わりがち",
        luagate: "VPC・RDS・ALB含む本番相当の環境を構築",
      },
      {
        label: "質問対応",
        selfStudy: "Stateの競合やプロバイダーエラーの解決が困難",
        luagate: "IaC経験豊富なメンターが設計から運用まで指導",
      },
      {
        label: "就職サポート",
        selfStudy: "IaCスキルの実務経験の証明が難しい",
        luagate: "実践プロジェクトの成果物で即戦力をアピール",
      },
    ],
  },
  faq: [
    {
      question: "Terraformを学ぶ前にAWSの知識は必要ですか？",
      answer:
        "はい、TerraformはAWSなどのクラウドリソースをコードで管理するツールなので、基本的なクラウドの知識は前提となります。ゆめさくエンジニアではAWSの基礎から段階的に学べるため、クラウド未経験の方でも安心です。",
    },
    {
      question: "TerraformとAWS CloudFormationのどちらを学ぶべきですか？",
      answer:
        "マルチクラウド対応と汎用性の観点から、Terraformをおすすめします。CloudFormationはAWS専用ですが、Terraformは AWS・GCP・Azureなど幅広いプロバイダーに対応しており、転職時にもスキルが活きます。",
    },
    {
      question: "Terraformの学習にプログラミング経験は必要ですか？",
      answer:
        "HCLは宣言型の設定言語なので、従来のプログラミングとは異なります。ただし、変数や条件分岐などの基本的なプログラミング概念を理解していると学習がスムーズです。",
    },
    {
      question: "Terraformの学習にAWSの利用料金はかかりますか？",
      answer:
        "AWSの無料利用枠を活用すれば、学習段階のコストは最小限に抑えられます。ゆめさくエンジニアでは費用を抑えながら効果的に学ぶ方法も指導しています。使い終わったリソースをterraform destroyで確実に削除する習慣も身につけます。",
    },
  ],
  relatedLanguages: [
    { slug: "aws", name: "AWS" },
    { slug: "kubernetes", name: "Kubernetes" },
    { slug: "docker", name: "Docker" },
    { slug: "linux", name: "Linux" },
  ],
};
